import { Link, useLocation } from 'react-router-dom';
import Icon, { type IconName } from './Icon';
import { useSessionStore } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';

/**
 * BottomNav — ported from the design reference. Tailwind classes preserved
 * exactly per build plan ticket 0B.5.
 *
 * The prototype used local `tab` state to swap views. The real version uses
 * React Router — each tab is a <Link> and the active state is derived from
 * the current pathname.
 *
 * Role-aware entries: the Review tab is conditionally rendered against
 * `inbound_email.review_queue.view`. From 3A.6 ship through 3D.2 this was a
 * manager+ permission and the nav rendered 4 tabs for staff / 5 for
 * manager+. 3D.3 (2026-05-15) flipped review_queue.view to any_member per
 * the posture policy — all roles now see 5 tabs. The conditional rendering
 * is preserved because 7D's permission_grants table may later let a clinic
 * tighten this gate back on a per-clinic basis. The gate type is kept
 * narrow so the next addition is an explicit code change rather than a
 * silent widening.
 *
 * Badge slot remains placeholder — re-enables in a later ticket once a
 * count source (pending review items, pending reorder rows, ...) is
 * wired in. Adding it for one nav entry without the others would
 * create inconsistent affordances.
 */

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** When set, hide the item unless the session holds this permission. */
  gate?: 'inbound_email.review_queue.view';
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/scan', label: 'Scan', icon: 'scan' },
  { to: '/inventory', label: 'Inventory', icon: 'inventory' },
  { to: '/reorder', label: 'Reorder', icon: 'cart' },
  {
    to: '/review',
    label: 'Review',
    icon: 'mail',
    gate: 'inbound_email.review_queue.view',
  },
  // Update #9 Wave 1 #2 (2026-07-17): Inventory Health swapped in for
  // Exports. Exports isn't functional and won't be by the pilot dinner —
  // no dead-end tab during a live demo. The /exports ROUTE stays
  // reachable by URL (screen kept for reference); only the nav slot
  // moves. Bring Exports back once it's actually built. Label is
  // "Health" per the 2026-07-16 mockup revision (screenshots doc) —
  // the spec's longer "Inventory Health" doesn't fit a 5-slot nav.
  // audit.view defaults to any_member, so no gate is needed here.
  { to: '/audit', label: 'Health', icon: 'heart' },
] as const;

export default function BottomNav() {
  const { pathname } = useLocation();
  // Read optionally — BottomNav also renders inside LoadingShell
  // before the session is set. In that case no gated items show
  // (correct conservative default for a loading-state chrome).
  const session = useSessionStore((s) => s.session);

  const visibleItems = NAV_ITEMS.filter((it) => {
    if (!it.gate) return true;
    if (!session) return false;
    return hasPermission(session, it.gate);
  });

  return (
    <nav
      className="bg-white border-t border-slate-200 px-1 pt-1.5 flex items-stretch"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
    >
      {visibleItems.map((it) => {
        const isActive = pathname === it.to || pathname.startsWith(`${it.to}/`);
        return (
          <Link
            key={it.to}
            to={it.to}
            className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg transition ${
              // Update #9 §1 nav colors: active = teal-dark icon+label on
              // a --app-bg tint; inactive = cool desaturated gray-teal
              // (replaces the old slate + plain teal treatment).
              isActive
                ? 'text-navactive bg-surface'
                : 'text-navmuted hover:text-navactive'
            }`}
          >
            <div className="relative">
              <Icon name={it.icon} className="w-5 h-5" strokeWidth={isActive ? 2 : 1.75} />
              {/* Badge slot — re-enabled when a count source is wired in */}
            </div>
            <span className={`text-[10px] ${isActive ? 'font-semibold' : 'font-medium'}`}>
              {it.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
